import type { ReactNode } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { setupWizardStepLabel } from '@/lib/setup-wizard'
import type { SetupWizardFunnel } from '@/services/dashboard'

const RANGE_DAYS: Record<string, number> = { '1m': 30, '3m': 90, '6m': 180, '12m': 365 }

/** The dashboard's period pill as the days the setup wizard summary takes. */
export const daysForRange = (range: string) => RANGE_DAYS[range] ?? 30

const STEPS = [1, 2, 3] as const

interface SetupWizardFunnelCardProps {
    /** null while loading. */
    funnel: SetupWizardFunnel | null
    /** Shown in place of the numbers when the request failed. */
    error: ReactNode
}

/** How many agencies started the setup wizard in the period, reached each step, and how it ended. */
export function SetupWizardFunnelCard({ funnel, error }: SetupWizardFunnelCardProps) {
    return (
        <Card className="py-0 gap-0">
            <CardHeader className="py-6 gap-0 border-b">
                <CardTitle className="text-lg font-semibold">Setup wizard</CardTitle>
                <CardDescription>
                    {funnel ? `Last ${funnel.days} days, ` : ''}agencies at each point of the wizard
                </CardDescription>
            </CardHeader>
            <CardContent className="pt-6 pb-6">
                {error ? error : funnel ? <FunnelRows funnel={funnel} /> : <FunnelSkeleton />}
            </CardContent>
        </Card>
    )
}

function FunnelRows({ funnel }: { funnel: SetupWizardFunnel }) {
    // Every share is of the agencies that started; an agency that started before the period can still reach a step in
    // it, so the largest count stands in when it is bigger.
    const base = Math.max(funnel.started, funnel.reachedStep[1], funnel.testLeads, funnel.published, funnel.leftEarly)
    if (base === 0) {
        return (
            <p className="py-8 text-center text-sm text-muted-foreground">
                No agency used the setup wizard in the last {funnel.days} days.
            </p>
        )
    }

    const row = (label: string, count: number) => <FunnelRow key={label} label={label} count={count} base={base} />
    return (
        <div className="space-y-4">
            <div className="space-y-2.5">
                {row('Started', funnel.started)}
                {STEPS.map((step) => row(setupWizardStepLabel(step), funnel.reachedStep[step]))}
            </div>
            <div className="space-y-2.5 border-t pt-4">
                {row('Test lead sent', funnel.testLeads)}
                {row('Published', funnel.published)}
                {row('Left early', funnel.leftEarly)}
            </div>
        </div>
    )
}

function FunnelRow({ label, count, base }: { label: string; count: number; base: number }) {
    const share = Math.round((count / base) * 100)
    return (
        <div
            className="flex items-center gap-3 text-sm"
            title={`${label}: ${count.toLocaleString()} ${count === 1 ? 'agency' : 'agencies'} (${share}%)`}
        >
            <span className="w-32 shrink-0 truncate text-muted-foreground">{label}</span>
            <div className="h-2 flex-1 rounded-full bg-muted" aria-hidden="true">
                <div className="h-2 rounded-full bg-primary" style={{ width: `${share}%` }} />
            </div>
            <span className="w-10 shrink-0 text-right font-medium tabular-nums">{count.toLocaleString()}</span>
            <span className="w-10 shrink-0 text-right text-xs text-muted-foreground tabular-nums">{share}%</span>
        </div>
    )
}

function FunnelSkeleton() {
    return (
        <div className="space-y-2.5">
            {Array.from({ length: 7 }).map((_, i) => (
                <Skeleton key={i} className="h-5 w-full" />
            ))}
        </div>
    )
}
